import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';
import styled, {css} from 'styled-components';
import i18n from 'i18next';

import {spacing} from 'styles/globals';

import {pxToRem} from 'utils/ui/units';
import {fillParent} from 'styles/mixins';
import Box from 'components/Box';
import Button from 'components/Button';

import SimplePagination from 'components/SimplePagination';

const Slide = styled.ul`
	position: relative;
	display: flex;

	/*
	 * Replace default focus outline with a themed one
	 * that only appears when the keyboard is used
	 */
	&:focus,
	&.focus-visible {
		outline: none;
	}

	&.focus-visible {
		&::after {
			content: '';
			${fillParent}
			${p => p.theme.globals.z.raised};

			border: 3px solid ${p => p.theme.links};
			border-radius: ${p => pxToRem(p.theme.globals.borderRadius.large)};
		}
	}
`;

const Item = styled.li`
	width: ${p => p.width * 100 || 100}%;

	${p =>
		p.gutterWidth &&
		css`
			padding-right: ${p => spacing[p.gutterWidth]};
		`}
	${p =>
		p.verticalSpacing &&
		css`
			padding-top: ${p => spacing[p.verticalSpacing]};
			padding-bottom: ${p => spacing[p.verticalSpacing]};
		`}
`;

const NavButton = styled(Button).withConfig({
	shouldForwardProp: prop => !['pos', 'lookAhead'].includes(prop),
})`
	position: absolute;
	top: 35%;
	z-index: ${p => p.theme.globals.z.raised};

	${p =>
		p.pos === 'left' &&
		css`
			left: 0;
			transform: translateX(-50%);
		`}
	${p =>
		p.pos === 'right' &&
		css`
			right: 0;
			transform: translateX(50%) scale(-1);

			${p =>
				p.lookAhead &&
				css`
					margin-right: ${spacing[p.lookAhead]};
				`}
		`}

	/* Visually hide disabled buttons unless they were focused */
	&.is-disabled:not(.focus-visible) {
		pointer-events: none;
		opacity: 0;
	}

	/* Don't show on small screens */
	@media (max-width: ${p => p.theme.globals.breakpoints.s}) {
		display: none;
	}
`;

/* Prevent elements from next slide from
 * becoming clickable by covering them up
 * Clicking this will navigate to next slide */
const LookAheadCover = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0;

	width: ${p => spacing[p.lookAhead]};

	${p =>
		p.gutterWidth &&
		css`
			margin-right: -${spacing[p.gutterWidth]};
		`}
`;

function splitIntoGroups(groupLength = 3, items) {
	return items
		.map((item, index) =>
			index % groupLength === 0
				? items.slice(index, index + groupLength)
				: null
		)
		.filter(item => item);
}

const springConfig = {
	duration: '0.54s',
	easeFunction: 'cubic-bezier(0.5,0,0.1,1)',
	delay: '0s',
};

/**
 * Automatically sets keyboard focus to the current slide
 * when navigating the carousel.
 */

function useSlideFocusHandler(currentSlide) {
	const containerRef = useRef();
	const slideRefs = useRef({});

	// Store a reference to every slide in the carousel
	function getSlideRef(index) {
		return element => {
			if (!slideRefs.current[index]) {
				slideRefs.current[index] = element;
			}
		};
	}

	useEffect(() => {
		// Set focus to the current slide, but only if keyboard
		// focus currently resides somewhere within the carousel
		// container element.
		// This is needed to prevent focus from being moved
		// directly to the carousel upon page load, and to
		// avoid conflicts between multiple carousels on a page

		const container = containerRef.current;
		const shouldHandleFocus =
			container && container.contains(document.activeElement);
		const currentSlideEl = slideRefs.current[currentSlide];

		if (shouldHandleFocus && currentSlideEl) {
			currentSlideEl.focus();
		}
	}, [currentSlide]);

	return {
		containerRef,
		getSlideRef,
	};
}

function Carousel(props) {
	const {
		items,
		maxItems,
		itemsPerSlide,
		gutterWidth,
		lookAhead,
		animateHeight,
		keyBy,
		'aria-labelledby': ariaLabelledBy,
		verticalSpacing,
		children,
	} = props;

	const [currentSlide, setCurrentSlide] = useState(0);
	const {containerRef, getSlideRef} = useSlideFocusHandler(currentSlide);

	function goToSlide(slide) {
		setCurrentSlide(slide);
	}

	function goToPrevSlide() {
		setCurrentSlide(prevState => prevState - 1);
	}

	function goToNextSlide() {
		setCurrentSlide(prevState => prevState + 1);
	}

	const hasItems = items && items.length > 0;
	const limitedItems = maxItems ? items.slice(0, maxItems) : items;
	const segmentedItems =
		hasItems && splitIntoGroups(itemsPerSlide, limitedItems);
	const slideCount = segmentedItems.length;
	const isAtStart = currentSlide === 0;
	const isAtEnd = currentSlide + 1 === slideCount;

	useEffect(() => {
		// If the number of slides is reduced, we need to make sure
		// that currentSlide doesn't point to a non-existent slide
		if (currentSlide > slideCount - 1) {
			setCurrentSlide(slideCount - 1);
		}
	}, [currentSlide, slideCount]);

	return (
		<Box
			ref={containerRef}
			position="relative"
			role="group"
			aria-roledescription="carousel"
			aria-labelledby={ariaLabelledBy}
		>
			<NavButton
				pos="left"
				round
				icon="arrow"
				aria-label={i18n.t('pagination_previous')}
				isDisabled={isAtStart}
				onClick={goToPrevSlide}
			/>
			<SwipeableViews
				resistance
				animateHeight={animateHeight}
				index={currentSlide}
				style={{
					marginRight: gutterWidth ? `-${spacing[gutterWidth]}` : '',
					paddingRight: lookAhead ? spacing[lookAhead] : '',
					/* Dumb fix for Chrome rounding errors in nested items: */
					borderLeft: '1px solid transparent',
				}}
				springConfig={springConfig}
			>
				{segmentedItems.map((slide, i) => {
					const isCurrentSlide = currentSlide === i;
					return (
						<Slide
							key={i}
							ref={getSlideRef(i)}
							tabIndex={-1}
							role="group"
							aria-roledescription="slide"
							aria-label={i18n.t(
								'pagination_current_page_of_total',
								{
									current: i + 1,
									count: slideCount,
								}
							)}
							aria-describedby={ariaLabelledBy}
						>
							{slide.map(item => (
								<Item
									key={item[keyBy]}
									width={1 / itemsPerSlide}
									gutterWidth={gutterWidth}
									verticalSpacing={verticalSpacing}
								>
									{children(item, isCurrentSlide)}
								</Item>
							))}
						</Slide>
					);
				})}
			</SwipeableViews>
			{lookAhead && !isAtEnd && (
				<LookAheadCover
					lookAhead={lookAhead}
					gutterWidth={gutterWidth}
					onClick={goToNextSlide}
				/>
			)}
			<NavButton
				pos="right"
				lookAhead={lookAhead}
				round
				icon="arrow"
				aria-label={i18n.t('pagination_next')}
				isDisabled={isAtEnd}
				onClick={goToNextSlide}
			/>
			{slideCount > 1 && (
				<Box pt="xxs">
					<SimplePagination
						zeroBased
						pageCount={slideCount}
						currentPage={currentSlide}
						getButtonLabel={count =>
							i18n.t('pagination_current_page', {count})
						}
						onChange={goToSlide}
					/>
				</Box>
			)}
		</Box>
	);
}

Carousel.defaultProps = {
	animateHeight: true,
	keyBy: 'id',
};

Carousel.propTypes = {
	items: PropTypes.array.isRequired,
	itemsPerSlide: PropTypes.number.isRequired,
	gutterWidth: PropTypes.string.isRequired,
	maxItems: PropTypes.number,
	lookAhead: PropTypes.string,
	animateHeight: PropTypes.bool,
	keyBy: PropTypes.string.isRequired,
	verticalSpacing: PropTypes.string,
	'aria-labelledby': PropTypes.string.isRequired,
};

export default Carousel;
